// packages
import dayjs from 'dayjs';

// components
import FormInputSection from './components/FormInputSection/FormInputSection';
import FormActionBar from './components/FormActionBar/FormActionBar';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import FormHeader from './components/FormHeader/FormHeader';

// form
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import schema from './AppointmentForm.schema';

// hooks
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityListener } from 'components/mrp/mrp.hooks';

// types
import {
  AppointmentFormFieldData,
  AppointmentRecurrenceType
} from './AppointmentForm.types';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';

// stores
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useShallow } from 'zustand/shallow';

// utils
import { appointmentFormFieldEntitiesToPlanningEntityIdentifiers } from './AppointmentForm.utils';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { isPrimaryModifier } from 'components/shared/Typography/Kbd/Kbd.utils';

// styles
import { AppointmentFormContainer } from './AppointmentForm.styles';

// components
import { CustomSelectRef } from 'components/shared/Select';
import { Container } from './AppointmentFormContainer.styles';

const AppointmentForm = () => {
  const { t } = useTranslation('common');
  const formContainerRef = useRef<HTMLDivElement>(null);
  const activityFieldRef = useRef<HTMLInputElement>(null);
  const recurrenceFieldRef = useRef<CustomSelectRef>(null);

  const calendarIdsPerEntityId = useCalendarStore(
    useShallow((state) => state.calendarIdsPerEntityId)
  );
  const closeCalendar = useCalendarStore(
    useShallow((state) => state.control.closeCalendar)
  );

  const {
    appointment,
    original,
    showCloseWarning,
    dirty,
    setDirty,
    closeAppointment,
    cancelCloseAppointment,
    setPreviewData
  } = useAppointmentStore(
    useShallow((state) => ({
      appointment: state.appointment,
      original: state.original,
      showCloseWarning: state.showCloseWarning,
      dirty: state.dirty,
      setDirty: state.control.setDirty,
      closeAppointment: state.control.closeAppointment,
      cancelCloseAppointment: state.control.cancelCloseAppointment,
      setPreviewData: state.control.setPreviewData
    }))
  );

  const { startDate } = useSelectedDateRange();
  const defaultDate = useMemo(() => {
    const weekStart = dayjs(startDate).startOf('day');
    const tomorrow = dayjs().add(1, 'day').startOf('day');
    return weekStart > tomorrow ? weekStart : tomorrow;
  }, [startDate]);

  const defaultValues: AppointmentFormFieldData = useMemo(
    () =>
      schema.cast({
        start: defaultDate.add(9, 'hours').toISOString(),
        end: defaultDate.add(9, 'hours').add(30, 'minutes').toISOString(),
        recurrencePattern: {
          daysSelected: [defaultDate.isoWeekday() - 1]
        },
        editAppointmentType: null,
        notePublic: '',
        noteInternal: '',
        isRecurring: false,
        activity: null,
        planEntities: {
          EMPLOYEE: [],
          CLIENT: [],
          WORKPLACE: [],
          RESOURCE: []
        },
        ...appointment
      }),

    // defaultDate is not stable to add to dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointment]
  );

  const methods = useForm<AppointmentFormFieldData>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    getValues,
    formState: { dirtyFields }
  } = methods;
  const [start, end, recurrencePattern] = useWatch({
    control: methods.control,
    name: ['start', 'end', 'recurrencePattern']
  });

  const [clients, employees, resources, workplace] = useWatch({
    control: methods.control,
    name: [
      'planEntities.CLIENT',
      'planEntities.EMPLOYEE',
      'planEntities.RESOURCE',
      'planEntities.WORKPLACE'
    ]
  });

  const selectedPlanningEntityIdentifiers = useMemo(
    () =>
      appointmentFormFieldEntitiesToPlanningEntityIdentifiers([
        ...clients,
        ...employees,
        ...resources,
        ...workplace
      ]),
    [clients, employees, resources, workplace]
  );

  const refetchIdentifiers = useMemo(() => {
    return (
      [
        ...selectedPlanningEntityIdentifiers,
        ...(!!original ? original.planningEntityIdentifiers : [])
      ]
        // Only refetch entities of which the calendar is opened
        .filter(({ entityId }) =>
          Object.keys(calendarIdsPerEntityId).some(
            (calendarEntityId) => calendarEntityId === entityId
          )
        )
    );
  }, [selectedPlanningEntityIdentifiers, original, calendarIdsPerEntityId]);

  const selectedEntityIds = useMemo(
    () => selectedPlanningEntityIdentifiers.map((entity) => entity.entityId!),
    [selectedPlanningEntityIdentifiers]
  );

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: refetchIdentifiers
  });

  const { openDialog, onClose: onCloseDialog } = useContext(DialogContext);

  const saveAppointment = useCallback(
    async (data: AppointmentFormFieldData) => {
      const current = appointment?.recurrencePattern;
      const next = data.recurrencePattern;

      const recurrencePatternChanged =
        appointment?.isRecurring &&
        (next?.recurrenceRule !== current?.recurrenceRule ||
          next?.recurrenceInterval !== current?.recurrenceInterval ||
          next?.recurrenceRangeType !== current?.recurrenceRangeType ||
          (next?.recurrenceRangeType === 'END_ON_DATE' &&
            next?.recurrenceEnd !== current?.recurrenceEnd));

      const updatedData = {
        ...data,
        originalOccurrenceStart: appointment?.start,
        ...(recurrencePatternChanged && {
          editAppointmentType: AppointmentRecurrenceType.THIS_AND_ALL_SUBSEQUENT
        })
      };

      openDialog?.(
        'smart-action-dialog-appointmentform-update',
        DialogType.SMART_ACTION_DIALOG,
        {
          actionType: appointment?.id ? 'update' : 'create',
          appointmentExceptions: appointment?.appointmentExceptions,
          payload: updatedData
        }
      );
    },
    [appointment, openDialog]
  );

  const closeAppointmentForm = useCallback(
    (forceClose: boolean) => {
      const calendarIds = selectedEntityIds.flatMap(
        (x) => calendarIdsPerEntityId[x]
      );

      calendarIds.forEach((id) => {
        // only close unpinned calendars
        closeCalendar(id);
      });

      closeAppointment(forceClose);
    },
    [selectedEntityIds, calendarIdsPerEntityId, closeCalendar, closeAppointment]
  );

  // Helper function to check if event originated from within the form
  const isEventFromForm = useCallback(
    (event: KeyboardEvent): boolean => {
      return (
        formContainerRef.current !== null &&
        event.target instanceof Node &&
        formContainerRef.current.contains(event.target)
      );
    },
    [formContainerRef]
  );

  useEffect(() => {
    setPreviewData({
      id: getValues('id'),
      start: getValues('start')!,
      end: getValues('end')!,
      entityIds: selectedEntityIds,
      recurrencePattern: recurrencePattern
    });
  }, [
    start,
    end,
    clients,
    employees,
    resources,
    workplace,
    recurrencePattern,
    setPreviewData,
    getValues,
    selectedEntityIds
  ]);

  useEffect(() => {
    const hasDirtyFields = Object.keys(dirtyFields).length > 0;
    if (dirty !== hasDirtyFields) setDirty(hasDirtyFields);
  }, [dirty, dirtyFields, setDirty]);

  useEffect(() => {
    if (appointment) {
      methods.reset(defaultValues);

      // Focus recurrence field if new appointment, otherwise activity field
      requestAnimationFrame(() => {
        if (appointment.id) {
          activityFieldRef.current?.focus();
        } else {
          recurrenceFieldRef.current?.focus();
        }
      });
    }
  }, [appointment, defaultValues, methods]);

  useEffect(() => {
    onCloseDialog?.(
      'smart-action-dialog-appointmentform-update',
      DialogType.SMART_ACTION_DIALOG,
      (props) => {
        if (props?.ok) {
          refetchByPlanningEntityIdentifiers([...refetchIdentifiers]);
          closeAppointmentForm(true);
        }
      }
    );
  }, [
    onCloseDialog,
    appointment,
    refetchByPlanningEntityIdentifiers,
    refetchIdentifiers,
    closeAppointmentForm
  ]);

  // Close form on esc (only when focus is within the form and no dropdowns are open)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only handle if event originated from Appointment Form
        if (isEventFromForm(event)) {
          closeAppointmentForm(false);
        }
      }
    };

    // Attach listener to document but check containment
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeAppointmentForm, isEventFromForm]);

  // Submit form on PRIMARY + S (only when focus is within the form)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 's' && isPrimaryModifier(event)) {
        // Only handle if event originated from Appointment Form
        if (isEventFromForm(event)) {
          // Prevent browser's default save dialog
          event.preventDefault();
          event.stopPropagation();

          handleSubmit((data) => saveAppointment(data))();
        }
      }
    };

    // Attach listener to document but check containment
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit, isEventFromForm, saveAppointment]);

  return (
    <Container>
      <FormProvider {...methods}>
        {/* prevents default form submission on ENTER but still uses form semantics */}
        <form onSubmit={(e) => e.preventDefault()} style={{ height: '100%' }}>
          <AppointmentFormContainer ref={formContainerRef}>
            <FormHeader />

            <FormInputSection
              activityFieldRef={activityFieldRef}
              recurrenceFieldRef={recurrenceFieldRef}
            />

            <FormActionBar
              onSave={handleSubmit((data) => saveAppointment(data))}
              onCancel={() => closeAppointmentForm(false)}
            />
          </AppointmentFormContainer>
        </form>
      </FormProvider>

      {showCloseWarning && (
        <ConfirmationDialog
          handleAction={(isConfirmed) => {
            if (isConfirmed) {
              closeAppointmentForm(true);
            } else {
              cancelCloseAppointment();
            }
          }}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={t('mrp.appointmentForm.confirmCloseTitle')}
        >
          <p>{t('mrp.appointmentForm.confirmCloseMessage')}</p>
        </ConfirmationDialog>
      )}
    </Container>
  );
};

export default AppointmentForm;
